import { BrowserRouter, Navigate, Route, Routes } from 'react-router'
import { Home } from './auth/Home'
import { MagicLinkLanding } from './auth/MagicLinkLanding'
import { SignInPage } from './auth/SignInPage'
import { EntraComplete } from './auth/EntraComplete'
import { ConsentReview } from './auth/ConsentReview'
import { FlowFrame } from './auth/FlowFrame'
import { ConnectTenant } from './auth/ConnectTenant'
import { OnboardingFlow } from './onboarding/OnboardingFlow'
import { ShellPlaceholder } from './app/ShellPlaceholder'
import { NAV_DESTINATIONS } from './app/tenants'
import { PrimitivesPage } from './PrimitivesPage'

/**
 * The fixture prototype is mounted only in a development build.
 *
 * It was on the catch-all route, which meant two things at once: a production deploy would have
 * served a fabricated 68/100 security score at the site root, and any URL the API redirected to
 * that the front end did not implement — `/auth/sign-in?error=entra_failed`, for one — rendered
 * the prototype landing page instead of the error. A failed sign-in looked like a marketing page.
 */
const SHOW_PROTOTYPE = import.meta.env.DEV

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* The application root: a gate that asks the server who this is and sends them where they
            belong. Without it `/` had no route, so every window.location.assign('/') — the
            workspace chooser's success path especially — fell through to the catch-all and bounced
            to sign-in, which is the multi-workspace sign-in loop. See Home. */}
        <Route path="/" element={<Home />} />

        {/* Where emailed sign-in links land. Consumes nothing on arrival — see the component. */}
        <Route path="/auth/magic-link" element={<FlowFrame step={0}><MagicLinkLanding /></FlowFrame>} />

        {/* Both of these are redirect targets the API already names. They existed in the
            server's code and not in the client's, which is the worst order to have it in. */}
        <Route path="/auth/sign-in" element={<SignInPage />} />
        <Route path="/auth/entra/complete" element={<FlowFrame step={0}><EntraComplete /></FlowFrame>} />

        {/* Where a signed-in customer starts admin consent. The API already redirected here on
            two paths -- outcome=refused and outcome=reported_error -- and the catch-all sent
            both to sign-in, so a customer who declined on Microsoft's page was told to sign in
            again. */}
        <Route path="/connect" element={<FlowFrame step={1} wide><ConnectTenant /></FlowFrame>} />

        {/* Where the consent callback sends the browser. Not a courtesy screen: the callback is
            unsigned and forgeable, so this is where a person looks at the directory that
            consented and makes the same-site POST that actually attaches it. */}
        <Route path="/connect/review" element={<FlowFrame step={1}><ConsentReview /></FlowFrame>} />

        {/* The shell's destinations. Dashboard and Recommendations read the real API; the rest
            say outright that they are not built, rather than showing an empty card that would
            read as "no findings". */}
        {NAV_DESTINATIONS.map((item) => (
          <Route key={item.path} path={item.path} element={<ShellPlaceholder />} />
        ))}

        {/* One finding, by the reference the product shows. Not in NAV_DESTINATIONS because it is
            not a place in the rail -- it is where a finding lives, and until it had a url of its
            own a finding could not be linked to, bookmarked, refreshed or left with the back
            button. The reference rather than the whole id, so what is on screen, what the search
            box takes and what is in the address bar are the same eight characters. */}
        <Route path="/findings/:reference" element={<ShellPlaceholder />} />

        {/* Settings' tabs, each at its own address (/settings/team). */}
        <Route path="/settings/:tab" element={<ShellPlaceholder />} />

        {/* Cloud (Azure). Not in NAV_DESTINATIONS: it exists only where Azure is assessed, which
            the session says (CloudArea sends everyone else to Recommendations, as /cloud did while
            there was no Cloud page). */}
        <Route path="/cloud" element={<ShellPlaceholder />} />

        {/* AI: likewise only where the AI area is on (AiArea sends everyone else to Recommendations). */}
        <Route path="/ai" element={<ShellPlaceholder />} />

        {/* AI assets (2026-10-12): every AI asset on one page, likewise only where the AI area is on. */}
        <Route path="/ai-assets" element={<ShellPlaceholder />} />

        {SHOW_PROTOTYPE ? (
          <>
            <Route path="/primitives" element={<PrimitivesPage />} />
            <Route path="/prototype" element={<OnboardingFlow />} />

            {/* The non-administrator path, which is the common case in a real tenant and
                otherwise only reachable through a link on the consent screen. */}
            <Route
              path="/prototype/standard-user"
              element={<OnboardingFlow signerIsAdmin={false} />}
            />
          </>
        ) : null}

        {/* Anything else goes to sign-in rather than to a screen full of fixtures. */}
        <Route path="*" element={<Navigate to="/auth/sign-in" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
